/**
 * Route guards for the studies section. A study has one canonical URL (/studies/$id) that renders
 * by status, so there's no stage → URL mapping here — just the limited-release gate and the
 * shared study loader the route's `beforeLoad` runs.
 */

import type { QueryClient } from "@tanstack/react-query";
import { redirect } from "@tanstack/react-router";
import { getStudy } from "@/features/studies/api";
import { studiesKeys } from "@/features/studies/queryKeys";
import { meQueryOptionsFor } from "@/hooks/useCloudChat";

/**
 * Limited-release gate: bounce to the research home unless the caller's org may use
 * studies (`me.studies_enabled`). The backend 403s every studies endpoint regardless;
 * this just keeps direct URL navigation from rendering a raw error. Called by every
 * studies `beforeLoad` (folded into `ensureStudy` for the per-study route).
 *
 * Pass `beforeLoad`'s `preload` flag so a hover-preloaded miss fetches speculatively
 * and can't sign the user out (TanStack `defaultPreload: "intent"` runs loaders on hover).
 */
export async function ensureStudiesEnabled(
  queryClient: QueryClient,
  preload = false
): Promise<void> {
  const me = await queryClient.ensureQueryData(meQueryOptionsFor(preload));
  if (!me.studies_enabled) throw redirect({ to: "/" });
}

/**
 * The /studies/$id `beforeLoad`: gate on studies access and warm the shared `["study", id]`
 * cache so the route component renders the status-appropriate view without a loading flash.
 *
 * Warming is best-effort: a data failure here must not fail the route. The view owns the
 * loading / not-found / retry states, so a momentary network drop shows an in-place "Try again"
 * instead of an error page (2026-09-18: a failed fetch here took the whole app down). Auth errors
 * swallowed here still reach `QueryCache.onError`, which redirects to /signin.
 */
export async function ensureStudy(
  queryClient: QueryClient,
  id: string,
  preload = false
): Promise<void> {
  const me = await queryClient.ensureQueryData(meQueryOptionsFor(preload));
  // A study link opened by LATO staff whose own workspace has no studies still resolves —
  // predictably, in the Admin area where staff manage every organization's studies.
  if (!me.studies_enabled && me.is_platform_admin)
    throw redirect({ to: "/admin/studies/$id", params: { id } });
  await ensureStudiesEnabled(queryClient, preload);
  await warmStudy(queryClient, id, preload);
}

/**
 * The Admin study route's loader: no workspace entitlement gate (the parent admin layout already
 * verified the platform admin; the backend authorizes the study for them), just the cache warm.
 */
export async function ensureStaffStudy(
  queryClient: QueryClient,
  id: string,
  preload = false
): Promise<void> {
  await warmStudy(queryClient, id, preload);
}

async function warmStudy(queryClient: QueryClient, id: string, preload: boolean): Promise<void> {
  await queryClient
    .ensureQueryData({
      queryKey: studiesKeys.detail(id),
      queryFn: () => getStudy(id, preload),
    })
    .catch(() => undefined);
}
